import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { TrainingCourseReportingListViewModel } from "./TrainingCourseReportingListView_model";
import { IF } from "../../../../../components/hoc/If";
import { PositionedSpinner } from "../../../../../components/ui/PositionedSpinner";
import { useCanViewField } from "../../../../../contexts/permissions/PermissionHooks";
import { PermissionFields } from "../../../../../contexts/permissions/PermissionsTypes";
import { BarChart } from "../../../../../core/charts/BarChart/BarChart";
import { TrainingCourseColDefFieldNamesEnum } from "../../../../../core/grids/builder/enums/AgGridColDefFieldNameEnum";
import I18n from "../../../../../core/localization/I18n";
import { ReportFilterView } from "../../../../../core/reportFilter/ReportFilter_view";
import { ReportFilterAddonsKeys } from "../../../../../core/reportFilter/ReportFilterAddon";
import Pages from "../../../../../routes/InsightRoutes";
import { useCurrentOrganisationId } from "../../../../../services/local/organisationContext/OrganisationContextModel";
import { ProjectPieChartPanel } from "../../Dashboard/pieChartPanel/ProjectPieChartPanel_view";
import "./_training-course-owner-chart.scss";
import { Panel } from "../../../../../components/ui/Panel";
import { RagColorMap, RagStatusString } from "../../../../../enums";
import { UrlOperators } from "../../../../../core/grids/builder/directorBuilder/grids/base/addons/FilterAddon/FilterAddonContants";

export const TrainingCourseReportingListView: React.FC = observer(() => {
  const organisationId = useCurrentOrganisationId();
  const { projectId, page } = useParams<{ projectId: string; page: string }>();
  const navigate = useNavigate();
  const [model, setModel] = useState(
    () => new TrainingCourseReportingListViewModel(organisationId, +projectId, navigate, page)
  );
  const canViewCourseReporting = useCanViewField(PermissionFields.ACTIONS, organisationId, +projectId);
  const ownerChartData = model.ownerChartData;
  const stakeholderOwnerChartData = model.stakeholderOwnerChartData;
  const trainingTypeChartData = model.trainingTypeChartData;
  const keys = [RagStatusString.RED, RagStatusString.AMBER, RagStatusString.GREEN, "completed", "unknown"];
  const colors = keys.map(k => RagColorMap[k]);
  const modelProjectId = model.projectId;

  useEffect(() => {
    model.onMount();
    return model.onUnmount();
  }, [model]);

  useEffect(() => {
    if (`${model.projectId}` !== projectId) {
      setModel(new TrainingCourseReportingListViewModel(organisationId, +projectId, navigate, page));
    }
  }, [projectId, modelProjectId, organisationId, model, navigate, page]);

  const courseRagOnClickFunction = (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
    if (!hasFilters) {
      const base = Pages.projects.training.index.generateLink(organisationId, projectId);
      const url = buildRagUrl(base, dataObject.id);
      navigate(url);
    }
  };

  const DrillThroughOnClickFunctionCoursesByOwnerRagStatus =
    (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
      if (!hasFilters) {
        const base = Pages.projects.training.index.generateLink(organisationId, projectId);
        let url = buildRagUrl(base, dataObject.id);
        url += `&${TrainingCourseColDefFieldNamesEnum.Owner}=${encodeURIComponent(dataObject.indexValue)}`;
        navigate(url);
      }
    };

  const DrillThroughOnClickFunctionCoursesByStakeholderOwnerRagStatus =
    (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
      if (!hasFilters) {
        const base = Pages.projects.training.index.generateLink(organisationId, projectId);
        let url = buildRagUrl(base, dataObject.id);
        url += `&${TrainingCourseColDefFieldNamesEnum.ProjectStakeholderOwners}=${encodeURIComponent(
          dataObject.indexValue
        )}`;
        navigate(url);
      }
    };

  const DrillThroughOnClickFunctionCoursesByTrainingTypeRagStatus =
    (organisationId, projectId, navigate, hasFilters: boolean) => (dataObject: any) => {
      if (!hasFilters) {
        const base = Pages.projects.training.index.generateLink(organisationId, projectId);
        let url = buildRagUrl(base, dataObject.id);
        url += `&${TrainingCourseColDefFieldNamesEnum.TrainingType}=${encodeURIComponent(dataObject.indexValue)}`;
        navigate(url);
      }
    };

  if (model.isLoading) {
    return <PositionedSpinner />;
  }

  return (
    <>
      <ReportFilterView
        addons={[ReportFilterAddonsKeys.CoursesGrid]}
        filterState={model.filterJSON}
        onClearClicked={model.resetLocalStorage}
        onFilterChange={model.onFilterChange}
      />
      <hr className="m-0" />
      <div className="container-fluid py-5">
        <div className="row mb-5">
          <div className="col-12 col-md-6 px-3 mb-4 mb-md-0">
            {/* Total Courses (Pie Chart) */}
            <ProjectPieChartPanel
              totalCount={model.totalCourses}
              onPieItemClick={courseRagOnClickFunction(organisationId, projectId, navigate, model.hasFilters)}
              data={model.ragData}
              colorKey={"ragColourArray"}
              shouldShowIf={canViewCourseReporting}
              label={I18n.t("phrases.totalCourses")}
              permissionLabel={I18n.t("phrases.noCourseReportingAccess")}
              noDataLabel={I18n.t("phrases.noCourses")}
              countUrl={Pages.projects.training.index.generateLink(organisationId, projectId)}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-6 px-3 mb-5">
            {/* Bar Chart By Course Owner */}
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.coursesByOwner")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && ownerChartData.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("phrases.noCoursesByOwner")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && ownerChartData.length > 0}>
                  <BarChart
                    data={ownerChartData}
                    keys={keys}
                    indexBy="label"
                    colors={colors}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    leftAxisLabel="Number of Courses"
                    onClick={DrillThroughOnClickFunctionCoursesByOwnerRagStatus(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 15,
                      maxLines: 2,
                      truncateAtChar: 15
                    })}
                    legendLabel={d => I18n.t(`phrases.${d.id}`)}
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
          <div className="col-12 col-md-6 px-3 mb-5">
            {/* Bar Chart By Course Stakeholder Owner */}
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.coursesByStakeholderOwner")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && stakeholderOwnerChartData.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("phrases.noCoursesByStakeholderOwner")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && stakeholderOwnerChartData.length > 0}>
                  <BarChart
                    data={stakeholderOwnerChartData}
                    keys={keys}
                    indexBy="label"
                    colors={colors}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    leftAxisLabel="Number of Courses"
                    onClick={DrillThroughOnClickFunctionCoursesByStakeholderOwnerRagStatus(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 10,
                      maxLines: 2,
                      truncateAtChar: 11
                    })}
                    legendLabel={d => I18n.t(`phrases.${d.id}`)}
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
          <div className="col-12 col-md-6 px-3 mb-5">
            {/* Bar Chart By Course Training Type */}
            <Panel.Panel hasBorder hasBorderRadius background={Panel.PanelBackgrounds.BG_LIGHT} className="p-3 mt-0">
              <h3>{I18n.t("visualisations.coursesByTrainingType")}</h3>
              <div className="training-course-owner-chart__wrapper">
                <IF condition={!model.isLoading && trainingTypeChartData.length === 0}>
                  <div className="training-course-owner-chart__data-wrapper">
                    <p className="mb-0">{I18n.t("phrases.noCoursesByTrainingType")}</p>
                  </div>
                </IF>
                <IF condition={!model.isLoading && trainingTypeChartData.length > 0}>
                  <BarChart
                    data={trainingTypeChartData}
                    keys={keys}
                    indexBy="label"
                    colors={colors}
                    tooltipLabelFn={({ indexValue }) => `${indexValue}`}
                    leftAxisLabel="Number of Courses"
                    onClick={DrillThroughOnClickFunctionCoursesByTrainingTypeRagStatus(
                      organisationId,
                      projectId,
                      navigate,
                      model.hasFilters
                    )}
                    chartMargin={{ top: 20, right: 100, bottom: 55, left: 50 }}
                    bottomAxisValueFormat={(val: string) => ({
                      value: val,
                      maxLineLength: 10,
                      maxLines: 2,
                      truncateAtChar: 11
                    })}
                    legendLabel={d => I18n.t(`phrases.${d.id}`)}
                  />
                </IF>
              </div>
            </Panel.Panel>
          </div>
        </div>
      </div>
    </>
  );
});

const buildRagUrl = (base: string, id: string): string => {
  if (id === "completed") {
    return `${base}?${TrainingCourseColDefFieldNamesEnum.TrainingProgress}=100`;
  }
  if (id === "unknown") {
    return `${base}?${TrainingCourseColDefFieldNamesEnum.RagStatus}=unknown&${TrainingCourseColDefFieldNamesEnum.TrainingProgress}=${UrlOperators.notEqual.symbol}100`;
  }
  return `${base}?${TrainingCourseColDefFieldNamesEnum.RagStatus}=${id}`;
};
